import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Experience {
  id: string
  name: string
  type: string
  score: number
  users: number
  completion_rate: number
  drop_off_points: string[]
  improvement_suggestions: string[]
}

interface Heatmap {
  id: string
  page: string
  clicks: number
  scroll_depth: number
  time_on_page: number
  bounce_rate: number
  engagement_score: number
}

interface Session {
  id: string
  user_id: string
  device: string
  pages_viewed: number
  duration_seconds: number
  actions: number
  outcome: string
  frustration_signals: number
}

interface Statistics {
  avg_experience_score: number
  total_sessions: number
  avg_session_duration: number
  conversion_rate: number
  bounce_rate: number
  engagement_rate: number
}

export default function ExperienceAnalyticsPage() {
  const [experiences, setExperiences] = useState<Experience[]>([])
  const [heatmaps, setHeatmaps] = useState<Heatmap[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [experiencesRes, heatmapsRes, sessionsRes, statsRes] = await Promise.all([
        fetch('/api/experienceanalytics/experiences'),
        fetch('/api/experienceanalytics/heatmaps'),
        fetch('/api/experienceanalytics/sessions'),
        fetch('/api/experienceanalytics/statistics')
      ])
      if (experiencesRes.ok) setExperiences(await experiencesRes.json())
      if (heatmapsRes.ok) setHeatmaps(await heatmapsRes.json())
      if (sessionsRes.ok) setSessions(await sessionsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching experience analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const getOutcomeBadge = (outcome: string) => {
    if (outcome === 'converted') return 'bg-green-100 text-green-800'
    if (outcome === 'engaged') return 'bg-blue-100 text-blue-800'
    if (outcome === 'bounced') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Experience Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 230 - User experience measurement and optimization</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Experience Score</p>
              <p className={`text-2xl font-bold ${getScoreColor(statistics.avg_experience_score)}`}>
                {statistics.avg_experience_score}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_sessions.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Duration</p>
              <p className="text-2xl font-bold text-blue-600">{formatDuration(statistics.avg_session_duration)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.conversion_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Bounce Rate</p>
              <p className="text-2xl font-bold text-red-600">{statistics.bounce_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Engagement Rate</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.engagement_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Experience Scores</h2>
          </div>
          <div className="p-6 space-y-4">
            {experiences.map((exp) => (
              <div key={exp.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-gray-900">{exp.name}</p>
                    <p className="text-sm text-gray-500">{exp.type} • {exp.users.toLocaleString()} users</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${getScoreColor(exp.score)}`}>{exp.score}</p>
                    <p className="text-xs text-gray-500">experience score</p>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Completion Rate</span>
                    <span>{exp.completion_rate}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className={`h-2 rounded-full ${getScoreBg(exp.completion_rate)}`}
                      style={{ width: `${exp.completion_rate}%` }}
                    />
                  </div>
                </div>
                {exp.drop_off_points.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Drop-off Points:</p>
                    <div className="flex flex-wrap gap-1">
                      {exp.drop_off_points.map((point, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">{point}</span>
                      ))}
                    </div>
                  </div>
                )}
                {exp.improvement_suggestions.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Suggestions:</p>
                    {exp.improvement_suggestions.slice(0, 2).map((sug, idx) => (
                      <p key={idx} className="text-xs text-purple-600">• {sug}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Page Heatmaps</h2>
            </div>
            <div className="p-6 space-y-4">
              {heatmaps.map((heatmap) => (
                <div key={heatmap.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{heatmap.page}</span>
                    <span className={`text-lg font-bold ${getScoreColor(heatmap.engagement_score)}`}>
                      {heatmap.engagement_score}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Clicks</p>
                      <p className="font-medium text-blue-600">{heatmap.clicks}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Scroll</p>
                      <p className="font-medium text-purple-600">{heatmap.scroll_depth}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Time</p>
                      <p className="font-medium text-green-600">{heatmap.time_on_page}s</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Bounce</p>
                      <p className="font-medium text-red-600">{heatmap.bounce_rate}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Recent Sessions</h2>
            </div>
            <div className="p-6 space-y-4">
              {sessions.map((session) => (
                <div key={session.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{session.user_id}</span>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{session.device}</span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getOutcomeBadge(session.outcome)}`}>
                      {session.outcome}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Pages</p>
                      <p className="font-medium text-gray-900">{session.pages_viewed}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Duration</p>
                      <p className="font-medium text-blue-600">{formatDuration(session.duration_seconds)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Actions</p>
                      <p className="font-medium text-purple-600">{session.actions}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Frustration</p>
                      <p className={`font-medium ${session.frustration_signals > 3 ? 'text-red-600' : 'text-green-600'}`}>
                        {session.frustration_signals}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
